/**
 * Single or bulk .docx import. Conversion runs here in the browser; each
 * result is shown with its report before anything is created. The Worker then
 * validates each template again and stores the original file as a backup.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ImportNote, ImportResult } from '@saledeed/docx-import';
import { api } from '../api/client';
import type { TemplateSummary } from '../api/types';
import { Badge, Button, ErrorBox, Modal, Notice } from '../components/ui';

const MAX_FILES = 25;
const MAX_BYTES = 10 * 1024 * 1024;
const NATURES = ['Sale Deed', 'Sale Agreement With Possession', 'Sale Agreement Without Possession'];

interface Item {
  file: File;
  bytes: Uint8Array | null;
  result: ImportResult | null;
  error: string | null;
  created: TemplateSummary | null;
  createError: unknown;
}

const LEVEL_TONE = { lost: 'red', changed: 'amber', info: 'slate' } as const;
const LEVEL_TEXT = { lost: 'Needs attention', changed: 'Changed', info: 'Note' } as const;

function Notes({ notes }: { notes: ImportNote[] }) {
  if (!notes.length) return <p className="text-xs text-green-800">Imported with no changes.</p>;
  return (
    <ul className="flex flex-col gap-1">
      {notes.map(n => (
        <li key={n.code} className="text-xs">
          <Badge tone={LEVEL_TONE[n.level]}>{LEVEL_TEXT[n.level]}</Badge>{' '}
          {n.message}{n.count > 1 ? ` (${n.count}×)` : ''}
          {n.examples.length > 0 && <span className="block pl-2 text-slate-500">e.g. “{n.examples.join('”, “')}”</span>}
        </li>
      ))}
    </ul>
  );
}

export function ImportDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (created: TemplateSummary[]) => void }) {
  const qc = useQueryClient();
  const [items, setItems] = useState<Item[]>([]);
  const [nature, setNature] = useState('Sale Deed');
  const [busy, setBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const reset = () => { setItems([]); setAcknowledged(false); };

  const choose = async (files: FileList | null) => {
    if (!files?.length) return;
    const picked = [...files].slice(0, MAX_FILES);
    setBusy(true);
    setAcknowledged(false);
    // Loaded on demand: the converter (mammoth) is only needed here.
    const { importDocx } = await import('@saledeed/docx-import');
    const out: Item[] = [];
    for (const file of picked) {
      const item: Item = { file, bytes: null, result: null, error: null, created: null, createError: null };
      if (file.size > MAX_BYTES) item.error = 'Larger than 10 MB.';
      else {
        try {
          item.bytes = new Uint8Array(await file.arrayBuffer());
          item.result = await importDocx(item.bytes, { fileName: file.name, natureOfDocument: nature });
        } catch {
          item.error = 'Could not read this file.';
        }
      }
      out.push(item);
      setItems([...out]);
    }
    setBusy(false);
  };

  const ready = items.filter(i => i.result?.ok && i.result.template && !i.created);
  const anyLost = ready.some(i => i.result!.notes.some(n => n.level === 'lost'));

  const create = async () => {
    setBusy(true);
    const next = [...items];
    for (const item of next) {
      if (!item.result?.ok || !item.result.template || item.created) continue;
      try {
        const tpl = { ...item.result.template, natureOfDocument: nature };
        const { template } = await api<{ template: TemplateSummary }>('POST', '/api/templates', { body: { template: tpl } });
        // Keep the original file as a backup next to the template.
        await api('PUT', `/api/templates/${template.id}/source`, { raw: item.bytes!.slice().buffer });
        item.created = template;
      } catch (e) {
        item.createError = e;
      }
      setItems([...next]);
    }
    setBusy(false);
    await qc.invalidateQueries({ queryKey: ['templates'] });
    const created = next.filter(i => i.created).map(i => i.created!);
    if (created.length && next.every(i => i.created || !i.result?.ok)) onDone(created);
  };

  return (
    <Modal open={open} wide title="Import Word documents" onClose={() => { if (!busy) { reset(); onClose(); } }}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          Choose one or more <strong>.docx</strong> files. Each is converted here and shown with a report of anything that changed.
          Nothing is saved until you press <em>Create</em>. Put <code>{'{{Field name}}'}</code> in the Word text to create fields automatically.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-sm">Files
            <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple disabled={busy}
              onChange={e => { void choose(e.target.files); e.target.value = ''; }} className="text-sm" />
          </label>
          <label className="flex flex-col text-sm">Nature of document
            <select className="rounded border border-slate-300 px-2 py-1" value={nature} onChange={e => setNature(e.target.value)} disabled={busy}>
              {NATURES.map(n => <option key={n}>{n}</option>)}
            </select>
          </label>
        </div>

        {items.length > 0 && (
          <ul className="flex flex-col gap-3" aria-live="polite">
            {items.map((item, i) => (
              <li key={i} className="rounded border border-slate-200 p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <strong className="text-sm">{item.file.name}</strong>
                  {item.created ? <Badge tone="green">Created · {item.created.templateCode}</Badge>
                    : item.error || (item.result && !item.result.ok) ? <Badge tone="red">Cannot import</Badge>
                    : item.result ? <Badge tone="blue">{item.result.template!.blocks.length} blocks · {item.result.template!.fields.length} fields</Badge>
                    : <Badge>Reading…</Badge>}
                </div>
                {item.error && <p className="text-sm text-red-800">{item.error}</p>}
                {item.result && <Notes notes={item.result.notes} />}
                {item.result && !item.result.ok && item.result.issues.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-red-800">{item.result.issues.slice(0, 5).map((x, n) => <li key={n}>{x.message}</li>)}</ul>
                )}
                <ErrorBox error={item.createError} />
              </li>
            ))}
          </ul>
        )}

        {ready.length > 0 && (
          <div className="flex flex-col gap-2">
            {anyLost && (
              <Notice tone="warning">
                <label className="inline-flex items-start gap-2">
                  <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} />
                  Some content needs attention (marked in red). I will review it in the editor.
                </label>
              </Notice>
            )}
            <div className="flex justify-end">
              <Button tone="primary" disabled={busy || (anyLost && !acknowledged)} onClick={() => void create()}>
                {busy ? 'Working…' : `Create ${ready.length} draft${ready.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
